/* ---- Bottom info panel for map page ---- */

import { ICONS } from '../icons';
import type { LocationData, SpoofingStatus } from '../types';
import { isDetectorWarningLike } from './SpoofingAlert';
import { renderSpoofingStatusList } from './SpoofingStatusList';

/** SpoofingStatus 上の検知器キー (LocationInfoPanel の警告サマリ集計用) */
const DETECTOR_KEYS = [
  'doppler',
  'imu',
  'signal_quality',
  'time',
  'signal_structure',
  'pressure_altitude',
  'doppler_raim',
  'qzss_authentication',
  'mock_location',
] as const;

export function renderLocationInfoPanel(
  location: LocationData | null,
  address: string,
  spoofingStatus: SpoofingStatus | null,
  currentGeofenceNames?: string[],
  addressConfidence?: number,
  geofenceCandidates?: { name: string; probability: number; inSchedule?: boolean }[],
  /** Phase 2-b: 環境推定 (INDOOR / OUTDOOR / UNKNOWN) */
  environment?: string,
  /** Phase 2-b: 環境推定信頼度 (HIGH / MEDIUM / LOW) */
  environmentAccuracy?: string,
  /** Phase 4: 位置欺瞞検知の詳細リストを展開状態で描画するか (アコーディオン初期状態) */
  spoofingDetailExpanded = false,
  /** Phase 4: 詳細リスト開閉のトグル通知 (呼び出し側で状態保持し、再描画時に反映) */
  onToggleSpoofingDetail?: (expanded: boolean) => void,
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'ip';

  // Phase 4 P3-iOS: availability=false の検知器は警告候補から除外。
  // (iOS で実測されない検知器を「未対応」と「異常」で混同しないため)
  const hasWarning = spoofingStatus
    ? DETECTOR_KEYS.some((k) => isDetectorWarningLike(spoofingStatus, k))
    : false;

  const lat = location?.lat?.toFixed(4) ?? '--';
  const lng = location?.lng?.toFixed(4) ?? '--';
  const alt = location?.alt?.toFixed(1) ?? '--';
  // Android Location.getAccuracy(): 1σ ≒ 68% 信頼半径 (メートル)
  const acc = typeof location?.accuracy === 'number' ? location.accuracy.toFixed(1) : '--';
  // Phase 2-b: 環境推定 (AnalyzeInOut の SNR ベース判定)
  const envText =
    environment && environment !== 'UNKNOWN'
      ? environment === 'INDOOR'
        ? '屋内'
        : '屋外'
      : '不明';
  const envAccText = environmentAccuracy ?? 'LOW';

  // 住所表示（confidence付き）
  const confPct =
    typeof addressConfidence === 'number' && addressConfidence > 0
      ? Math.round(addressConfidence * 100)
      : 0;
  const addressDisplay = address || '位置情報を取得中...';

  // ジオフェンス確率表示（候補がある場合のみ）
  // Phase 4: inSchedule=false (時間外) はグレー文字 + "(時間外)" ラベル付加
  // 0.5% 未満 (= 表示上 0%) の候補は除外。SDK 側で全 polygon を投げてくるため無関係エリアも来うる
  let geofenceHtml = '';
  const visibleCandidates = (geofenceCandidates ?? []).filter((g) => g.probability >= 0.005);
  if (visibleCandidates.length > 0) {
    const items = visibleCandidates
      .map((g) => {
        const pct = Math.round(g.probability * 100);
        const active = g.inSchedule !== false;
        const iconColor = active ? '#1D4ED8' : '#9CA3AF';
        const nameColor = active ? '#1E40AF' : '#6B7280';
        const suffix = active
          ? ''
          : '<span style="color:#9CA3AF;font-size:11px;margin-left:2px;">(時間外)</span>';
        return `<div style="display:flex;align-items:center;gap:6px;padding:4px 0;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2"><polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5"/></svg>
        <span style="font-size:13px;color:${nameColor};font-weight:500;">${g.name}</span>
        <span style="color:#6B7280;font-size:11px;">(${pct}%)</span>
        ${suffix}
      </div>`;
      })
      .join('');
    geofenceHtml = `
      <div style="border-top:1px solid #E5E7EB;margin:6px 0;padding-top:6px;">
        <div style="font-size:11px;color:#6B7280;margin-bottom:2px;">エリア判定</div>
        ${items}
      </div>`;
  }

  el.innerHTML = `
    <div class="il">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#0066FF" stroke="none"><circle cx="12" cy="12" r="6"/></svg>
      <span>現在の位置情報</span>
    </div>
    <div class="ia">${addressDisplay}${confPct > 0 ? `<span style="color:#6B7280;font-size:11px;margin-left:4px;">(${confPct}%)</span>` : ''}</div>
    ${geofenceHtml}
    <div class="ic">
      <span>${lat}\u00B0 N, ${lng}\u00B0 E</span>
      <span>高度 <span class="av">${alt}</span> m</span>
      <span>精度 <span class="av">${acc}</span> m</span>
      <span>環境 <span class="av">${envText} (${envAccText})</span></span>
    </div>
    <button type="button" class="db db-toggle ${hasWarning ? 'w' : 'o'}" aria-expanded="${spoofingDetailExpanded}">
      ${hasWarning ? ICONS.shieldWarn : ICONS.shieldOk}
      <span style="color:${hasWarning ? '#DC2626' : '#059669'}">
        ${hasWarning ? '位置欺瞞検知: 注意' : '位置欺瞞検知: 正常'}
      </span>
      <svg class="db-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${hasWarning ? '#DC2626' : '#059669'}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
    </button>
    <div class="db-detail" style="display:${spoofingDetailExpanded ? 'block' : 'none'}"></div>
  `;

  // 展開状態の DOM 反映
  const toggleBtn = el.querySelector('.db-toggle') as HTMLButtonElement | null;
  const detailEl = el.querySelector('.db-detail') as HTMLDivElement | null;
  const chev = el.querySelector('.db-chev') as SVGElement | null;

  const renderDetail = () => {
    if (!detailEl) return;
    detailEl.innerHTML = '';
    if (spoofingStatus) {
      detailEl.appendChild(renderSpoofingStatusList(spoofingStatus));
    }
  };

  if (spoofingDetailExpanded) {
    renderDetail();
    if (chev) chev.style.transform = 'rotate(90deg)';
  }

  toggleBtn?.addEventListener('click', () => {
    if (!detailEl || !chev) return;
    const willOpen = detailEl.style.display === 'none';
    if (willOpen) {
      renderDetail();
      detailEl.style.display = 'block';
      chev.style.transform = 'rotate(90deg)';
      toggleBtn.setAttribute('aria-expanded', 'true');
    } else {
      detailEl.style.display = 'none';
      chev.style.transform = 'rotate(0deg)';
      toggleBtn.setAttribute('aria-expanded', 'false');
    }
    onToggleSpoofingDetail?.(willOpen);
  });

  return el;
}
